import React from 'react'
import { Link, usePage } from '@inertiajs/inertia-react'
import { Inertia } from '@inertiajs/inertia'
import Layout from '../../Shared/Layout/LayoutAdmin'
import LayoutSiaLearner from '../../Shared/Layout/LayoutSiaLearner'
import Breadcrumbs from '../../Shared/Components/Breadcrumbs'
import { getYouTubeEmbedSrc } from '../../utils/youtube'
import { isSpreadsheet } from '../../utils/siaFile'
import { SIA_NAVY } from '../../constants/siaTheme'

function VideoPlayer({ item }) {
    if (item?.is_mp4 || item?.video_url) {
        if (!item.video_url) {
            return (
                <div className="ratio ratio-16x9 bg-light d-flex align-items-center justify-content-center border rounded">
                    <span className="text-muted">Video pendiente de publicar</span>
                </div>
            )
        }
        return (
            <div className="ratio ratio-16x9 rounded overflow-hidden border bg-dark">
                <video
                    src={item.video_url}
                    controls
                    playsInline
                    controlsList="nodownload"
                    disablePictureInPicture
                    onContextMenu={(e) => e.preventDefault()}
                    onTimeUpdate={(e) => {
                        const video = e.currentTarget
                        if (!video.duration || video.currentTime / video.duration < 0.2) return
                        window.axios
                            .post(route('sia.item.progress', item.id), {
                                event: video.ended ? 'ended' : 'tick',
                                position_seconds: Math.floor(video.currentTime),
                                duration_seconds: Math.floor(video.duration),
                            })
                            .then(({ data }) => {
                                if (data?.completed) Inertia.reload({ preserveScroll: true })
                            })
                            .catch(() => {})
                    }}
                    className="w-100 h-100"
                    style={{ objectFit: 'contain' }}
                />
            </div>
        )
    }

    const src = getYouTubeEmbedSrc(item?.youtube_url)
    if (!src) {
        return (
            <div className="ratio ratio-16x9 bg-light d-flex align-items-center justify-content-center border rounded">
                <span className="text-muted">Video pendiente de publicar</span>
            </div>
        )
    }
    return (
        <div className="ratio ratio-16x9 rounded overflow-hidden border">
            <iframe
                title={item?.title || 'Video'}
                src={src}
                className="border-0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
            />
        </div>
    )
}

function linesFromText(text) {
    if (!text) return []
    return String(text)
        .split(/\r?\n/)
        .map((l) => l.trim())
        .filter(Boolean)
}

const LessonShow = ({ section, item, ancestors = [] }) => {
    const { sia_use_learner_layout } = usePage().props
    const path = `/admin/sia/${section.slug}/lecciones/${item.id}`
    const backHref = route('sia.section.show', section.slug)
    const highlights = linesFromText(item.highlights)
    const applications = linesFromText(item.application_text)

    const spreadsheet = isSpreadsheet(item)

    React.useEffect(() => {
        if (spreadsheet || item?.locked || item?.type !== 'document' || !item?.file_url) return undefined
        window.axios.post(route('sia.item.progress', item.id), { event: 'open' }).catch(() => {})
        return undefined
    }, [item, spreadsheet])

    return (
        <>
            {!sia_use_learner_layout && (
                <div className="mb-5 mt-5">
                    <Breadcrumbs currentPath={path} />
                </div>
            )}

            <div className="d-flex flex-wrap align-items-center gap-2 mb-4">
                <Link href={backHref} className="btn btn-sm btn-light">
                    ← Volver a {section.title}
                </Link>
                {ancestors.map((a) => (
                    <Link key={a.slug} href={route('sia.section.show', a.slug)} className="btn btn-sm btn-light-primary">
                        {a.title}
                    </Link>
                ))}
            </div>

            <div
                className="rounded-2 mb-8 py-5 px-4 text-center text-white text-uppercase fw-bold fs-3"
                style={{ backgroundColor: SIA_NAVY, letterSpacing: '0.02em' }}
            >
                {item.title}
            </div>

            <div className="card card-flush shadow-sm mb-8">
                <div className="card-body p-6 p-lg-8">
                    {item.locked && (
                        <div className="text-muted">
                            Este contenido está cerrado. Termina el anterior para abrirlo.
                        </div>
                    )}
                    {!item.locked && item.type !== 'document' && (
                        <div className="mb-8">
                            <VideoPlayer item={item} />
                        </div>
                    )}

                    <div className="row g-6 mb-6">
                        {item.author && (
                            <div className="col-md-6">
                                <div className="text-muted fs-7 text-uppercase fw-semibold mb-1">Autor</div>
                                <div className="fs-5 text-gray-900">{item.author}</div>
                            </div>
                        )}
                        {item.duration_label && (
                            <div className="col-md-6">
                                <div className="text-muted fs-7 text-uppercase fw-semibold mb-1">Tiempo</div>
                                <div className="fs-5 text-gray-900">{item.duration_label}</div>
                            </div>
                        )}
                    </div>

                    {item.main_topic && (
                        <div className="mb-6">
                            <div className="text-muted fs-7 text-uppercase fw-semibold mb-2">Tema principal</div>
                            <p className="fs-5 text-gray-800 mb-0 lh-lg">{item.main_topic}</p>
                        </div>
                    )}

                    {highlights.length > 0 && (
                        <div className="mb-6">
                            <div className="text-muted fs-7 text-uppercase fw-semibold mb-2">Puntos más importantes</div>
                            <ul className="fs-5 text-gray-800 ps-5 mb-0">
                                {highlights.map((line, idx) => (
                                    <li key={idx} className="mb-2">
                                        {line}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {applications.length > 0 && (
                        <div className="mb-6">
                            <div className="text-muted fs-7 text-uppercase fw-semibold mb-2">Aplicación del asesor</div>
                            <ul className="fs-5 text-gray-800 ps-5 mb-0">
                                {applications.map((line, idx) => (
                                    <li key={idx} className="mb-2">
                                        {line}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {item.description && (
                        <div className="mb-6">
                            <div className="text-muted fs-7 text-uppercase fw-semibold mb-2">Descripción</div>
                            <p className="fs-5 text-gray-800 mb-0 lh-lg">{item.description}</p>
                        </div>
                    )}

                    {!spreadsheet && !item.locked && (item.type === 'document' || item.file_url) && item.file_url && (
                        <div className="mb-6">
                            <div className="text-muted fs-7 text-uppercase fw-semibold mb-2">Documento</div>
                            <div className="border rounded overflow-hidden bg-light">
                                <iframe
                                    title={item.title}
                                    src={item.file_url}
                                    className="border-0 w-100"
                                    style={{ height: '70vh' }}
                                />
                            </div>
                        </div>
                    )}

                    <div className="d-flex flex-wrap gap-3">
                        {(item.has_download || item.file_path) && (
                            <a href={item.download_route || route('sia.download', item.id)} className="btn btn-primary">
                                {spreadsheet ? 'Descargar Excel' : 'Descargar material'}
                            </a>
                        )}
                        {item.download_url && (
                            <a
                                href={item.download_url}
                                target="_blank"
                                rel="noreferrer"
                                className="btn btn-light-primary"
                            >
                                Abrir enlace externo
                            </a>
                        )}
                    </div>
                </div>
            </div>

            <p className="text-center text-muted small mb-10">
                Copyright © EACM {new Date().getFullYear()} Todos los derechos reservados. SIA
            </p>
        </>
    )
}

LessonShow.layout = (page) => {
    const title = page.props.item?.title || 'Lección'
    if (page.props.sia_use_learner_layout) {
        return <LayoutSiaLearner title={`${title} | SIA`}>{page}</LayoutSiaLearner>
    }
    return <Layout title={title}>{page}</Layout>
}

export default LessonShow
